import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Filters, IndexTable, Pagination, Select, Stack } from "@shopify/polaris";
import { Badge, Spinner, ButtonGroup } from '@shopify/polaris'; // Adjust as necessary

import { useAppMutation, useDebounce } from "../../../../../../hooks/index.js";
import { useAppSelector } from "../../../../../../hooks/redux.js";

import { actionsTableHeadings } from "./helpers/actionsTableHeadings.js";
import { sortOptions } from "./helpers/sortOptions.js";
import { IActions } from "./interfaces/Actions.js";

import styles from "./ActionTable.module.css";

import EmptyTable from "./components/EmptyTable.js";
import CellWithName from "./components/CellWithName.js";
import SetActiveActionButton from "./components/SetActiveActionButton.js";
import ActionPopover from "./components/ActionPopover.js";

const ActionsTable = ({ selectedActionsType, active, handleSync, handleCreateAction, toggleUndoBanner, isUndoing, setShowBanner, undoActionIds, setIsDeleting }) => {
  const shop = useAppSelector((state) => state.shop.data);
  const [queryValue, setQueryValue] = useState("");
  const [actions, setActions] = useState([]);
  const [page, setPage] = useState(1);
  const [actionsCount, setActionsCount] = useState(0);
  const [sort, setSort] = useState("desc");
  const debouncedSearchTerm = useDebounce(queryValue, 1000);
  const pageSize = 10;

  const { isLoading, mutate } = useAppMutation<IActions>({
    url: "/api/actions",
    reactMutationOptions: {
      onSuccess(data: IActions) {
        setActions(() => data.actions);
        setActionsCount(() => data.count);
      }
    }
  });

  const getActions = useCallback(() => {
    setActions([]);
    mutate({
      actionsType: selectedActionsType,
      page: 1,
      pageSize,
      queryValue: debouncedSearchTerm,
      sort
    });
  }, [page, pageSize, selectedActionsType, debouncedSearchTerm, sort]);

  useEffect(() => {
    setPage(1);
    getActions();
  }, [selectedActionsType, debouncedSearchTerm, sort, active]);

  const handleSelectChange = useCallback((value: string) => setSort(value), []);

  const handleQueryValueRemove = useCallback(() => {
    setQueryValue('');
  }, []);

  const handleClearAll = useCallback(() => {
    handleQueryValueRemove();
  }, [handleQueryValueRemove]);

  const handlePagination = useCallback((isNext: boolean) => {
    mutate({
      actionsType: selectedActionsType,
      page: isNext ? page + 1 : page - 1,
      pageSize,
      queryValue: debouncedSearchTerm,
      sort
    });
    setPage(prev => isNext ? prev + 1 : prev - 1);
  }, [])

  const rowMarkup = useMemo(() => {
    return actions.map((action, index) => {
      return (
        <IndexTable.Row
          id={action.id}
          key={action.id}
          position={index}
        >
          <IndexTable.Cell>
            {<Badge status={action.isActive ? "success" : "new"}>{action.isActive ? "Enabled" : "Disabled"}</Badge>}
          </IndexTable.Cell>
          <IndexTable.Cell className={styles['limited-width-cell']}>
            <CellWithName action={action} />
          </IndexTable.Cell>
          <IndexTable.Cell>
            {action.hideProductsCount || '-'}
          </IndexTable.Cell>
          <IndexTable.Cell>
            {action.pushDownProductsCount || '-'}
          </IndexTable.Cell>
          {/* {selectedActionsType === ACTION_TYPE_STOCK && */}
          <IndexTable.Cell>{action.backInStockHideProducts || '-'}</IndexTable.Cell>
          {/* {selectedActionsType === ACTION_TYPE_STOCK && */}
          <IndexTable.Cell>{action.backInStockPushDownProducts || '-'}</IndexTable.Cell>
          <IndexTable.Cell>
            {action.allProductsCount || '-'}
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Stack alignment="center" distribution="trailing" wrap={false}>
              {(action?.updateStatus === "processing" || undoActionIds?.includes(action.id)) && <Spinner size="small" />}
              <ButtonGroup fullWidth noWrap>
                <SetActiveActionButton
                  actionId={action.id}
                  isActive={action.isActive}
                  onSetAction={setActions}
                  disabled={undoActionIds?.includes(action.id) || action.updateStatus === "processing"}
                />
                <ActionPopover
                  actionId={action.id}
                  onSetAction={setActions}
                  disabled={undoActionIds?.includes(action.id) || action.updateStatus === "processing"}
                  onUpdate={getActions}
                  toggleUndoBanner={toggleUndoBanner}
                  actionStatus={action.isActive}
                  isUndoing={isUndoing}
                  setIsDeleting={setIsDeleting}
                  setShowBanner={setShowBanner}
                />
              </ButtonGroup>
            </Stack>
          </IndexTable.Cell>
        </IndexTable.Row>
      );
    });
  }, [selectedActionsType, actions, mutate, setActions, isUndoing, undoActionIds]);

  return (
    <Card>
      <Card.Section>
        <div className={styles["filter-wrapper"]}>
          <Filters
            queryValue={queryValue}
            filters={[]}
            onQueryChange={setQueryValue}
            onQueryClear={handleQueryValueRemove}
            onClearAll={handleClearAll}
          />
          <div className={styles["select-wrapper"]}>
            <Select
              label=""
              labelHidden
              options={sortOptions}
              onChange={handleSelectChange}
              value={sort}
            />
          </div>
        </div>
      </Card.Section>
      <Stack vertical>
        <div className={styles["action-table-wrapper"]}>
          <IndexTable
            itemCount={actions.length}
            headings={actionsTableHeadings}
            selectable={false}
            loading={isLoading}
            emptyState={isLoading ?
              <div></div> :
              <EmptyTable
                shop={shop}
                actions={actions}
                queryValue={queryValue}
                handleSync={handleSync}
                handleCreateAction={handleCreateAction}
              />
            }
          >
            {rowMarkup}
          </IndexTable>
        </div>
        <Card.Section>
          <Pagination
            hasPrevious={page > 1}
            onPrevious={() => handlePagination(false)}
            hasNext={page * pageSize < actionsCount}
            onNext={() => handlePagination(true)}
          />
        </Card.Section>
      </Stack>
    </Card>
  );
};

export default ActionsTable;
